<template>
	<UiModalMobile
		v-model="modelValue"
		class="modal__mobile"
		:titleBtnSave="$t('Добавить')"
		:isDontNeedCloseWhenSave="true"
		@save="saveHandler"
		:is-loading-btn-save="props.isLoading"
		:z-index="zIndex"
	>
		<TypographyKTypographyBody2
			color="var(--color_font_black_wite)"
			font-type="bold"
		>
			{{ $t('Уведомления') }}
		</TypographyKTypographyBody2>
		<div class="wrapper">
			<div
				class="notifications"
				v-if="notificationsList?.collection?.length && !loading.getNotifications"
			>
				<div
					class="btn-read-all"
					@click="readAll"
				>
					прочитать все
				</div>
				<div
					class="notification"
					v-for="notification in notificationsList.collection"
					:key="notification.id"
				>
					<div class="notification__header">
						<div class="status-wrapper">
							<div
								class="status"
								:class="{ '--is-read': notification?.isRead }"
							></div>
							<TypographyKTypographyBody4 font-type="semi-bold">
								{{ notification?.title }}
							</TypographyKTypographyBody4>
						</div>
						<div class="date">
							<TypographyKTypographyBody4 font-type="regular">
								{{ getDateInLocaleString(notification?.createdAt, { showTime: true }) }}
							</TypographyKTypographyBody4>
						</div>
					</div>
					<div class="notification__body">
						<TypographyKTypographyBody4
							font-type="regular"
							class="message"
							v-html="notification?.message"
						>
						</TypographyKTypographyBody4>
					</div>
				</div>
			</div>
			<div
				v-else-if="!loading.getNotifications"
				class="notification-empty"
			>
				Уведомлений нет
			</div>
			<div
				v-else
				class="loader"
			>
				<UiLoader
					:circle-width="2"
					:radius="20"
					:color="'var(--primary)'"
				/>
			</div>
		</div>
		<template #custom-actions></template>
	</UiModalMobile>
</template>

<script setup lang="ts">
	import { TYPE_COMMENT } from '~/const/ui';
	import { getDateInLocaleString } from '~/helpers/date';
	import { VALIDATE_RULES, validator } from '~/helpers/validate';
	import { useNotificationStore } from '~/store/notification.store';
	import type { IDriverRequestsItem } from '~~/shared/interfaces/driverRequests';

	const emits = defineEmits(['save']);
	const modelValue = defineModel('modelValue', { required: true, type: Boolean });
	const notificationsStore = useNotificationStore();
	const notificationsList = storeToRefs(notificationsStore).notificationsRequest;
	const pageReqInterval = ref<NodeJS.Timeout | null>(null);
	const props = withDefaults(
		defineProps<{
			currentCargo: IDriverRequestsItem;
			isLoading?: boolean;
			zIndex?: number;
		}>(),
		{}
	);
	const text = ref<string>('');
	const typeCommentComputed = computed(() => {
		return Object.values(TYPE_COMMENT);
	});
	const importance = ref(typeCommentComputed.value[0] || null);
	const loading = reactive({
		getNotifications: false
	});

	const errorField = ref({
		text: { status: false, msg: '' }
	});

	const clearError = (type: string) => {
		errorField.value = { ...errorField.value, [type]: { status: false } };
	};

	const readAll = () => {
		console.log('readAll');
		notificationsStore.readAllNotifications();
	};

	const validateHandler = () => {
		const arrayRules: Array<{
			field: string;
			value: string | null;
			rules: VALIDATE_RULES[];
		}> = [{ field: 'text', value: text.value, rules: [VALIDATE_RULES.required] }];
		arrayRules.forEach((item) => {
			errorField.value = { ...errorField.value, [item.field]: validator(item.value, item.rules)[0] };
		});
	};

	const saveHandler = () => {
		validateHandler();
		if (!Object.values(errorField.value).find((item) => item.status)) {
			emits('save', {
				cargoId: props.currentCargo.id,
				text: text.value,
				importance: importance.value?.id
			});
		}
	};

	const getNotifications = async (isNeedLoader = true) => {
		if (isNeedLoader) {
			loading.getNotifications = true;
		}
		await notificationsStore.getNotifications();

		loading.getNotifications = false;
	};

	getNotifications();

	onMounted(() => {
		const startInterval = async () => {
			pageReqInterval.value = setTimeout(async () => {
				await getNotifications(false);
				if (pageReqInterval.value) startInterval();
			}, 60000);
		};
		startInterval();
	});

	onUnmounted(() => {
		if (pageReqInterval.value) {
			clearTimeout(pageReqInterval.value);
			pageReqInterval.value = null;
		}
	});
</script>

<style scoped lang="scss">
	.loader {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
	}
	.notification-empty {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 100%;
		font-size: 14px;
		font-weight: 700;
		color: var(--color_font_black_wite);
	}
	.btn-read-all {
		position: relative;
		margin-left: auto;
		color: #2e4a85;
		font-size: 10px;
		font-weight: 500;
		text-decoration: dotted;
		cursor: pointer;

		&:hover {
			&::after {
				content: '';
				position: absolute;
				left: 0;
				bottom: 0;
				width: 100%;
				height: 1px;
				border-bottom: 1px solid #2e4a85;
			}
		}
	}
	.notification__body {
		.message {
			background-color: #eaedf5;
			word-break: break-word;
			padding: 8px 14px;
			border-radius: 12px;
			margin-top: 8px;
		}
	}
	.status {
		width: 9px;
		height: 9px;
		border-radius: 50%;
		background-blend-mode: overlay;
		background-color: #14ab87;

		&.--is-read {
			background: linear-gradient(260.49deg, rgba(255, 255, 255, 0) -0.38%, rgba(255, 255, 255, 0.4) 153.72%);
			background: #37547b4d;
		}
	}
	.notifications {
		display: flex;
		flex-direction: column;
		gap: 10px;
		height: 100%;
		padding: 12px 13px;
		background-color: #eaedf5;
		border-radius: 12px;
		height: 100%;
		min-height: max-content;
	}
	.notification {
		width: 100%;
		background-color: var(--white);
		padding: 15px;
		border-radius: 16px;

		.notification__header {
			display: flex;
			align-items: center;
			gap: 4px;
			justify-content: space-between;

			.status-wrapper {
				display: flex;
				align-items: center;
				gap: 4px;
			}
		}
	}
	.modal__mobile {
		display: flex;
		flex-direction: column;

		.ui-select__item {
			display: flex;
			align-items: center;

			.icon {
				margin-right: 5px;
			}
		}

		.label-select {
			margin-left: auto;
		}

		.upper-limit-clue {
			max-width: 300px;
			@media (max-width: $sm) {
				max-width: 200px;
			}
		}

		.input__secret {
			display: flex;
			flex-direction: column;
			gap: 8px;
			margin-bottom: -10px;
		}

		.wrapper {
			display: flex;
			flex-direction: column;
			overflow: auto;
			gap: 17px;
			margin-top: 14px;
			padding-right: 10px;
			margin-right: -10px;
			padding-bottom: 5px;
			padding-top: 5px;
			height: 100%;
		}
	}
</style>
